import { useState } from "react";
import { Plus, Minus } from "lucide-react";
import { Reveal, Eyebrow } from "@/components/site/Primitives";
import { TREATMENT_CATEGORIES } from "@/data/site";

const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

export const TreatmentDetails = () => {
  const [activeCat, setActiveCat] = useState(TREATMENT_CATEGORIES[0].id);
  const [open, setOpen] = useState(0);

  const category = TREATMENT_CATEGORIES.find((c) => c.id === activeCat);

  return (
    <section id="behandlungen" className="relative py-24 lg:py-36 bg-cream">
      <div className="mx-auto max-w-5xl px-6 lg:px-10">
        <Reveal>
          <div className="max-w-2xl">
            <Eyebrow>Behandlungen im Detail</Eyebrow>
            <h2 className="mt-6 font-serif font-light tracking-tight text-charcoal text-4xl sm:text-5xl leading-tight">
              Alles Wissenswerte zu meinen Behandlungen
            </h2>
            <p className="mt-6 text-charcoal-soft leading-relaxed">
              Transparent und ehrlich – hier erfahren Sie Details zu Ablauf, Wirkung und wichtigen Hinweisen.
            </p>
          </div>
        </Reveal>

        {/* Category tabs */}
        <Reveal>
          <div className="mt-10 flex flex-wrap gap-3">
            {TREATMENT_CATEGORIES.map((c) => (
              <button
                key={c.id}
                data-testid={`treatment-tab-${c.id}`}
                onClick={() => {
                  setActiveCat(c.id);
                  setOpen(0);
                }}
                className={`rounded-full px-6 py-2.5 text-sm transition-colors duration-300 border ${
                  activeCat === c.id
                    ? "bg-charcoal text-gold-soft border-charcoal"
                    : "bg-transparent text-charcoal-soft border-gold/40 hover:bg-gold/10"
                }`}
              >
                {c.label}
              </button>
            ))}
          </div>
        </Reveal>

        {/* Accordion */}
        <div className="mt-8 divide-y divide-gold/20 border-y border-gold/20">
          {category.treatments.map((t, i) => {
            const isOpen = open === i;
            return (
              <div key={t.name} data-testid={`treatment-${activeCat}-${i}`}>
                <button
                  onClick={() => setOpen(isOpen ? -1 : i)}
                  className="w-full flex items-center justify-between gap-6 py-6 text-left group"
                  data-testid={`treatment-toggle-${activeCat}-${i}`}
                >
                  <div>
                    <h3 className="font-serif text-2xl lg:text-3xl text-charcoal font-light group-hover:text-gold-deep transition-colors duration-300">
                      {t.name}
                    </h3>
                    {t.meta && <p className="mt-1 text-xs uppercase tracking-widest text-gold-deep">{t.meta}</p>}
                  </div>
                  <span className="shrink-0 h-10 w-10 rounded-full border border-gold/40 flex items-center justify-center text-gold-deep">
                    {isOpen ? <Minus size={18} /> : <Plus size={18} />}
                  </span>
                </button>
                <div
                  className={`grid transition-[grid-template-rows,opacity] duration-500 ease-in-out ${
                    isOpen ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"
                  }`}
                >
                  <div className="overflow-hidden">
                    <div className="pb-8 max-w-3xl space-y-4">
                      <p className="text-charcoal leading-relaxed">{t.summary}</p>
                      {t.details.map((d, di) => (
                        <p key={di} className="text-charcoal-soft leading-relaxed text-sm">{d}</p>
                      ))}
                      <button
                        onClick={() => scrollTo("kontakt")}
                        data-testid={`treatment-cta-${activeCat}-${i}`}
                        className="mt-2 text-xs uppercase tracking-widest text-gold-deep border-b border-gold/50 pb-1 hover:text-charcoal transition-colors duration-300"
                      >
                        Beratung anfragen
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <p className="mt-10 text-xs text-charcoal-muted leading-relaxed max-w-3xl">
          Hinweis: Alle Angaben dienen der allgemeinen Information und stellen kein Heilversprechen dar.
          Ergebnisse können individuell variieren. Eine ausführliche Aufklärung erfolgt stets vor jeder Behandlung.
        </p>
      </div>
    </section>
  );
};
